import { groupBy } from "lodash-es";

import { CURATED_CRATES, type CuratedCrate } from "@garimpa/lib/curatedCrates";

export interface GarimposTimelineEntry<P> {
  crate: CuratedCrate;
  proof: P;
}

export interface GarimposTimelineDecade<P> {
  startYear: number;
  entries: GarimposTimelineEntry<P>[];
}

/**
 * The decades that have at least one rendered crate, oldest first; inside a decade the crates keep
 * their editorial `CURATED_CRATES` order. Proofs for unknown crate ids are ignored.
 */
export function buildGarimposTimeline<P extends { id: string }>(
  proofs: readonly P[],
): GarimposTimelineDecade<P>[] {
  const proofById = new Map(proofs.map((proof) => [proof.id, proof]));
  const entries = CURATED_CRATES.flatMap((crate) => {
    const proof = proofById.get(crate.id);
    return proof ? [{ crate, proof }] : [];
  });
  const byDecade = groupBy(entries, (entry) => entry.crate.startYear);

  return Object.keys(byDecade)
    .map(Number)
    .sort((left, right) => left - right)
    .map((startYear) => ({ startYear, entries: byDecade[startYear] ?? [] }));
}

/** Anchor id of a decade section; the rail links and tracks these. */
export function garimposDecadeId(startYear: number): string {
  return `garimpos-${startYear}`;
}

/** The rendered crates in page order (decade, then editorial order). */
export function garimposTimelineCrates<P>(
  timeline: readonly GarimposTimelineDecade<P>[],
): CuratedCrate[] {
  return timeline.flatMap((decade) => decade.entries.map((entry) => entry.crate));
}
